import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, Paper, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import type { HeatQualityIssue } from "src/store/api/generatedApi";
import { HeatQualityFlagRows } from "components/heatQualityIssues/HeatQualityFlagRows";

dayjs.extend(utc);

interface HeatQualityFlagsPanelProps {
  issues: HeatQualityIssue[];
  onSelectHeat?: (heatId: string, tapTimestamp: string | null) => void;
}

export const HeatQualityFlagsPanel = ({
  issues,
  onSelectHeat,
}: HeatQualityFlagsPanelProps) => {
  const { t } = useTenantTranslation();
  const flaggedIssues = issues.filter((issue) => issue.flags.length > 0);

  return (
    <Box sx={{ overflowY: "auto", maxHeight: 400, p: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t("heatQualityIssues")}
      </Typography>
      <Stack spacing={0.5}>
        {flaggedIssues.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("noQualityIssues")}
          </Typography>
        ) : null}
        {flaggedIssues.map((issue) => (
          <Paper
            key={issue.heat_id}
            elevation={1}
            sx={{
              p: 1,
              borderLeft: "4px solid",
              borderColor: "warning.main",
              cursor: onSelectHeat != null ? "pointer" : "default",
              "&:hover":
                onSelectHeat != null ? { bgcolor: "action.hover" } : undefined,
            }}
            onClick={
              onSelectHeat != null
                ? () => {
                    onSelectHeat(issue.heat_id, issue.tap_timestamp ?? null);
                  }
                : undefined
            }
          >
            <Stack direction="row" alignItems="flex-start" spacing={0.5}>
              <WarningAmberRoundedIcon
                fontSize="small"
                color="warning"
                sx={{ mt: 0.25, flexShrink: 0 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{ alignItems: "center", mb: 0.25 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {issue.heat_id}
                  </Typography>
                  {issue.tap_timestamp != null ? (
                    <Typography variant="caption" color="text.secondary">
                      · {t("tap")}{" "}
                      {dayjs.utc(issue.tap_timestamp).format("HH:mm")}
                    </Typography>
                  ) : null}
                  {issue.loading_start_time != null ? (
                    <Typography variant="caption" color="text.secondary">
                      · {t("load")}{" "}
                      {dayjs.utc(issue.loading_start_time).format("HH:mm")}
                    </Typography>
                  ) : null}
                </Stack>
                <HeatQualityFlagRows flags={issue.flags} />
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};
